/**
 * Shared typography and spacing for the Finances module.
 * Reference: Unclaimed commissions table (text-sm body, uppercase xs headers,
 * font-medium primary column). Slightly compact table row padding vs default shadcn.
 */
export const financesPage = {
  title: 'text-2xl font-bold tracking-tight',
  subtitle: 'text-sm text-muted-foreground',
  shell: 'p-6 max-w-[1600px] mx-auto space-y-6',
  shellCompact: 'p-6 max-w-[1600px] mx-auto space-y-4',
  /** Create / edit invoice sub-pages */
  formTitle: 'text-xl font-semibold tracking-tight',
} as const;

export const financesSection = {
  title: 'text-sm font-semibold',
  label: 'text-sm font-medium text-muted-foreground',
  eyebrow: 'text-xs font-medium uppercase tracking-wide text-muted-foreground',
} as const;

/** Apply on a wrapper around `<Table>` for compact finances data grids. */
export const financesTable = {
  shell:
    '[&_th]:h-10 [&_th]:px-2 [&_th]:py-2 [&_td]:px-2 [&_td]:py-2.5 md:[&_th]:px-3 md:[&_td]:px-3',
  border:
    'overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700',
  headerRow: 'bg-gray-50 dark:bg-gray-800/50',
  /**
   * AR-316: `min-h-11` below `md` — as a bare inline-flex the sort control was
   * only as tall as its 16px text, well under a thumb-sized target on a phone.
   */
  headSortable:
    'inline-flex min-h-11 items-center text-xs font-medium uppercase tracking-wide text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 md:min-h-0',
  headStatic:
    'text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400',
  primaryCell: 'font-medium',
  /** @deprecated Prefer standard font; kept for admin/technical displays only. */
  monoCell: 'font-mono',
  empty: 'text-sm text-muted-foreground',
  pagination: 'text-sm text-gray-600 dark:text-gray-400',
} as const;

export const financesFilter = {
  label: 'text-xs font-medium text-muted-foreground',
  sublabel: 'text-xs text-muted-foreground',
  input: 'h-8 text-sm',
  pill: 'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
  popoverTitle: 'text-sm font-semibold',
  alert: 'text-xs font-medium text-rose-600 dark:text-rose-400',
} as const;

/** Financial Snapshot KPI cards — label → hero value → goal → status footer. */
export const financesSnapshot = {
  card: 'rounded-xl border bg-card overflow-hidden flex flex-col',
  body: 'p-5 pb-4',
  footer:
    'bg-muted/40 px-5 py-3.5 flex items-start justify-between gap-3 mt-auto border-t border-border/50',
  /** Eyebrow above the hero number */
  label: 'text-sm font-medium uppercase tracking-wide text-muted-foreground',
  value: 'text-4xl font-bold tracking-tight text-foreground',
  goal: 'text-lg text-muted-foreground mt-0.5',
  subMetric: 'text-sm text-muted-foreground',
  statusOn: 'text-sm font-semibold text-green-700 dark:text-green-400',
  statusOff: 'text-sm font-semibold text-red-600 dark:text-red-400',
  statusDetail: 'text-sm text-muted-foreground break-words',
  footerEmpty: 'text-sm text-muted-foreground',
  /** Dual-metric cards (e.g. trips traveled + invoiced) */
  dualValue: 'text-3xl font-bold tracking-tight text-foreground',
  dualLabel: 'text-sm text-muted-foreground mt-0.5',
  /** Nested panels inside Revenue Snapshot */
  panel: 'rounded-xl bg-card border px-5 py-4',
  panelValue: 'text-3xl font-bold tracking-tight text-foreground leading-tight',
  panelCaption: 'text-sm text-muted-foreground text-center mt-3 pt-2 border-t border-dashed',
} as const;
